import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Eye, Trash2, CheckCircle, 
  XCircle, Clock, AlertCircle, Calendar, MapPin, User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import '../dashboard/Dashboard.module.css';
import { api } from '../../utils/api';
import AdminLayout from '../common/AdminLayout';
import Loader from '../common/Loader';

interface Inquiry {
  id: number;
  inquiry_reference: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  is_read: boolean;
  scheduled_date: string;
  scheduled_time: string;
  location_address: string;
  created_at: string;
  category_name: string;
  client_first_name: string;
  client_last_name: string;
}

const InquiriesManagement: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    is_read: 'all',
    search: '',
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',
    direction: 'desc',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');

  const { admin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInquiries();
  }, [currentPage, filters, sortConfig]);

  const fetchInquiries = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        status: filters.status,
        is_read: filters.is_read,
        sort_by: sortConfig.field,
        sort_order: sortConfig.direction,
      });

      const data = await api.get(`/api/admin/inquiries?${params}`);
      
      if (data.success) {
        setInquiries(data.inquiries);
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.totalItems);
      } else {
        setError('Failed to load inquiries');
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError('Failed to load inquiries. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInquiries();
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewInquiry = (id: number) => {
    navigate(`/admin/inquiries/${id}`);
  };

  const confirmDelete = (id: number) => {
    setInquiryToDelete(id);
    setShowDeleteModal(true);
    setDeleteError('');
    setDeleteSuccess('');
  };

  const handleDelete = async () => {
    if (!inquiryToDelete) return;
    
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      const data = await api.delete(`/api/admin/inquiries/${inquiryToDelete}`);
      
      if (data.success) {
        setDeleteSuccess('Inquiry deleted successfully');
        setInquiries(inquiries.filter(inquiry => inquiry.id !== inquiryToDelete));
        setTotalItems(prev => prev - 1);
        setTimeout(() => {
          setShowDeleteModal(false);
          setDeleteSuccess('');
        }, 1500);
      } else {
        setDeleteError('Failed to delete inquiry');
      }
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      setDeleteError('Failed to delete inquiry. Please try again later.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'low':
        return <span className="badge bg-info">Low</span>;
      case 'medium':
        return <span className="badge bg-primary">Medium</span>;
      case 'high':
        return <span className="badge bg-warning">High</span>;
      case 'urgent':
        return <span className="badge bg-danger">Urgent</span>;
      default:
        return <span className="badge bg-secondary">{priority}</span>;
    }
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="badge bg-info d-flex align-items-center gap-1"><AlertCircle size={12} /> New</span>;
      case 'in_progress':
        return <span className="badge bg-warning d-flex align-items-center gap-1"><Clock size={12} /> In Progress</span>;
      case 'completed':
        return <span className="badge bg-success d-flex align-items-center gap-1"><CheckCircle size={12} /> Completed</span>;
      case 'cancelled':
        return <span className="badge bg-danger d-flex align-items-center gap-1"><XCircle size={12} /> Cancelled</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString: string) => {
    // Format time from "14:30:00" to "2:30 PM"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  return (
    <AdminLayout title="Service Inquiries" subtitle="Manage and track service inquiries from customers">
      <div className="inquiries-management">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => fetchInquiries()}>
              Refresh
            </button>
          </div>
        </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                          <div className="d-flex flex-wrap gap-3 mb-3 mb-md-0">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    style={{ width: '150px' }}
                    className="form-select"
                  >
                    <option value="all">All Statuses</option>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Read Status</label>
                  <select
                    name="is_read"
                    value={filters.is_read}
                    onChange={handleFilterChange}
                    style={{ width: '150px' }}
                    className="form-select"
                  >
                    <option value="all">All</option>
                    <option value="false">Unread</option>
                    <option value="true">Read</option>
                  </select>
                </div>
              </div>

              <form onSubmit={handleSearchSubmit}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search inquiries..."
                    value={filters.search}
                    onChange={handleSearchChange}
                  />
                  <button type="submit" className="btn btn-primary">
                    <Search size={18} />
                  </button>
                </div>
              </form>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <Loader message="Loading inquiries..." />
          ) : inquiries.length === 0 ? (
            <div className="alert alert-info">No inquiries found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => handleSort('inquiry_reference')}
                        style={{ cursor: 'pointer' }}
                      >
                        Reference #
                      </th>
                      <th>Title</th>
                      <th>Client</th>
                      <th>Category</th>
                      <th 
                        onClick={() => handleSort('scheduled_date')}
                        style={{ cursor: 'pointer' }}
                      >
                        Date
                      </th>
                      <th 
                        onClick={() => handleSort('priority')}
                        style={{ cursor: 'pointer' }}
                      >
                        Priority
                      </th>
                      <th 
                        onClick={() => handleSort('status')}
                        style={{ cursor: 'pointer' }}
                      >
                        Status
                      </th>
                      <th 
                        onClick={() => handleSort('created_at')}
                        style={{ cursor: 'pointer' }}
                      >
                        Created
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map(inquiry => (
                      <tr key={inquiry.id} className={!inquiry.is_read ? 'table-primary' : ''}>
                        <td>{inquiry.inquiry_reference}</td>
                        <td>
                          {inquiry.title.length > 30 
                            ? `${inquiry.title.substring(0, 30)}...` 
                            : inquiry.title}
                        </td>
                        <td>
                          {inquiry.client_first_name} {inquiry.client_last_name}
                        </td>
                        <td>{inquiry.category_name}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Calendar size={14} className="me-1" />
                            {formatDate(inquiry.scheduled_date)}
                          </div>
                          <div className="small text-muted">
                            {formatTime(inquiry.scheduled_time)}
                          </div>
                        </td>
                        <td>{renderPriorityBadge(inquiry.priority)}</td>
                        <td>{renderStatusBadge(inquiry.status)}</td>
                        <td>
                          <div>{formatDate(inquiry.created_at)}</div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleViewInquiry(inquiry.id)}
                            >
                              <Eye size={16} />
                            </button>
                            <button 
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => confirmDelete(inquiry.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {inquiries.length} of {totalItems} inquiries
                </div>
                <nav>
                  <ul className="pagination">
                    <li className="page-item">
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(1)} 
                        disabled={currentPage === 1}
                      >
                        First
                      </button>
                    </li>
                    <li className="page-item">
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }
                      
                      return (
                        <li key={pageNum} className="page-item">
                          <button
                            className={`page-link ${pageNum === currentPage ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className="page-item">
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                    <li className="page-item">
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(totalPages)} 
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                {deleteSuccess ? (
                  <div className="alert alert-success">{deleteSuccess}</div>
                ) : (
                  <>
                    <p>Are you sure you want to delete this inquiry?</p>
                    <p className="text-danger"><strong>This action cannot be undone.</strong></p>
                    {deleteError && <div className="alert alert-danger">{deleteError}</div>}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {!deleteSuccess && (
                  <>
                    <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                      Cancel
                    </button>
                    <button 
                      className="btn btn-danger" 
                      onClick={handleDelete}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default InquiriesManagement;